import * as Yup from 'yup';
import { validateField, ValidationType } from './formValidation';
import type { FamilyMember } from 'types';

export const emptyMember: Partial<FamilyMember> = {
    accompanying: false,
    birth_date: '',
    country_of_birth: '',
    country_of_residence: '',
    firstname: '',
    lastname: '',
    middlename: '',
    nationality: '',
    relation: '',
};

/** Non-child family members (AssessmentV2 FamilyData). */
const familyMemberItemSchema = Yup.object({
    relation: validateField(ValidationType.STRING),
    birth_date: validateField(ValidationType.STRING),
    country_of_residence: validateField(ValidationType.STRING),
    // Shown only on the UK domain (AssessmentV2 parity) — optional.
    country_of_birth: validateField(ValidationType.STRING, false),
    nationality: validateField(ValidationType.STRING),
    firstname: validateField(ValidationType.STRING),
    lastname: validateField(ValidationType.STRING),
    middlename: validateField(ValidationType.STRING, false),
    accompanying: Yup.boolean().nullable(),
});

/** Children (AssessmentV2 ChildData) — names optional except relation/dates. */
const childItemSchema = Yup.object({
    relation: validateField(ValidationType.STRING),
    birth_date: validateField(ValidationType.STRING),
    country_of_residence: validateField(ValidationType.STRING),
    nationality: validateField(ValidationType.STRING),
    accompanying: Yup.boolean().required(),
    // First/last name required only when the child is accompanying (AssessmentV2 parity).
    firstname: Yup.string()
        .nullable()
        .when('accompanying', {
            is: true,
            then: (schema) => schema.required('This is a required field'),
            otherwise: (schema) => schema.nullable(),
        }),
    lastname: Yup.string()
        .nullable()
        .when('accompanying', {
            is: true,
            then: (schema) => schema.required('This is a required field'),
            otherwise: (schema) => schema.nullable(),
        }),
    middlename: validateField(ValidationType.STRING, false),
    country_of_birth: validateField(ValidationType.STRING, false),
});

export const familyMembersValidationSchema = Yup.object({
    hasAccompanyingFamilyMembers: Yup.boolean().nullable(),
    hasChildren: Yup.boolean().nullable(),
    family_members: Yup.array().when('hasAccompanyingFamilyMembers', {
        is: true,
        then: (schema) => schema.of(familyMemberItemSchema).min(1),
        otherwise: () => Yup.array().notRequired(),
    }),
    children: Yup.array().when('hasChildren', {
        is: true,
        then: (schema) => schema.of(childItemSchema).min(1),
        otherwise: () => Yup.array().notRequired(),
    }),
});
